import React, { useState, useEffect, useRef } from 'react';
import { marshmallowService } from '../services';
import html2canvas from 'html2canvas';
import BackButton from '../components/BackButton';
import '../styles/App.css';

const MarshmallowAdmin = () => {
  const [marshmallows, setMarshmallows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingId, setReplyingId] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mode, setMode] = useState('view'); // 'view', 'export', 'delete'
  const cardRefs = useRef({});
  const exportRefs = useRef({});

  useEffect(() => {
    fetchMarshmallows();
  }, []);

  const fetchMarshmallows = async () => {
    try {
      const data = await marshmallowService.getAllMarshmallows();
      setMarshmallows(data);
    } catch (error) {
      console.error('Error fetching marshmallows:', error);
    } finally {
      setLoading(false);
    }
  };

  const enterMode = (newMode) => {
    setMode(newMode);
    setSelectedIds(new Set());
  };

  const cancelMode = () => {
    setMode('view');
    setSelectedIds(new Set());
  };

  const toggleExpand = async (id, isRead) => {
    // In selection mode, clicking the card toggles selection instead of expanding
    if (mode !== 'view') {
      toggleSelect(id);
      return;
    }

    const newExpandedIds = new Set(expandedIds);
    if (newExpandedIds.has(id)) {
      newExpandedIds.delete(id);
    } else {
      newExpandedIds.add(id);
      if (!isRead) {
        try {
          await marshmallowService.markAsRead(id);
          // Update local state to reflect read status
          setMarshmallows(prev => prev.map(m => 
            m.id === id ? { ...m, user_read_at: new Date().toISOString() } : m
          ));
        } catch (error) {
          console.error('Error marking as read:', error);
        }
      }
    }
    setExpandedIds(newExpandedIds);
  };

  const toggleSelect = (id) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(id)) {
      newSelectedIds.delete(id);
    } else {
      newSelectedIds.add(id);
    }
    setSelectedIds(newSelectedIds);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === marshmallows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(marshmallows.map(m => m.id)));
    }
  };

  const handleReplyClick = (e, m) => {
    e.stopPropagation();
    setReplyingId(m.id);
    setReplyContent(m.reply_content || '');
  };

  const handleCancelReply = () => {
    setReplyingId(null);
    setReplyContent('');
  };

  const handleSubmitReply = async (id) => {
    try {
      await marshmallowService.replyMarshmallow(id, replyContent);
      setReplyingId(null);
      fetchMarshmallows();
    } catch (error) {
      console.error('Error replying:', error);
      alert('回复失败');
    }
  };

  const handleConfirmAction = async () => {
    if (selectedIds.size === 0) return;

    if (mode === 'delete') {
      if (!window.confirm(`确定要删除选中的 ${selectedIds.size} 个棉花糖吗？`)) return;

      try {
        await marshmallowService.deleteMarshmallows(Array.from(selectedIds));
        fetchMarshmallows();
        cancelMode();
      } catch (error) {
        console.error('Error deleting marshmallows:', error);
        alert('删除失败');
      }
    } else if (mode === 'export') {
      const idsToExport = Array.from(selectedIds);
      
      for (const id of idsToExport) {
        const element = exportRefs.current[id];
        if (element) {
          try {
            const canvas = await html2canvas(element, {
                backgroundColor: null,
                scale: 2,
                logging: false,
                useCORS: true
            });
            
            const link = document.createElement('a');
            link.download = `marshmallow-${id}.png`;
            link.href = canvas.toDataURL();
            link.click();
          } catch (err) {
            console.error(`Error exporting marshmallow ${id}:`, err);
          }
        }
      }
      cancelMode();
    }
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="container">
      <BackButton to="/marshmallows" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>棉花糖管理</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {mode === 'view' ? (
            <>
              <button 
                className="btn btn-secondary" 
                onClick={() => enterMode('export')}
              >
                批量导出
              </button>
              <button 
                className="btn" 
                style={{ background: '#e74c3c', color: 'white' }}
                onClick={() => enterMode('delete')}
              >
                批量删除
              </button>
            </>
          ) : (
            <>
              <span style={{ display: 'flex', alignItems: 'center', marginRight: '1rem', fontWeight: 'bold' }}>
                已选择: {selectedIds.size}
              </span>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmAction}
                disabled={selectedIds.size === 0}
                style={{ background: mode === 'delete' ? '#e74c3c' : 'var(--primary-purple)' }}
              >
                {mode === 'delete' ? '确认删除' : '确认导出'}
              </button>
              <button 
                className="btn btn-outline" 
                onClick={cancelMode}
              >
                取消
              </button>
            </>
          )}
        </div>
      </div>

      {mode !== 'view' && (
        <div style={{ marginBottom: '1rem', animation: 'fadeIn 0.3s' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={selectedIds.size === marshmallows.length && marshmallows.length > 0}
              onChange={toggleSelectAll}
              style={{ width: '18px', height: '18px' }}
            />
            <span>全选</span>
          </label>
        </div>
      )}
      
      <div className="grid" style={{ gridTemplateColumns: '1fr' }}>
        {marshmallows.map((m) => {
          const isExpanded = expandedIds.has(m.id);
          const isRead = !!m.user_read_at;
          const isSelected = selectedIds.has(m.id);
          
          return (
            <div 
              key={m.id} 
              ref={el => cardRefs.current[m.id] = el}
              className="card" 
              style={{ 
                borderLeft: m.reply_content ? '5px solid #27ae60' : '5px solid #e74c3c',
                background: isRead ? '#ffffff' : '#fff8e1',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: isSelected ? '2px solid var(--primary-purple)' : undefined,
                transform: isSelected ? 'scale(1.01)' : undefined
              }}
              onClick={() => toggleExpand(m.id, isRead)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                {mode !== 'view' && (
                  <div onClick={(e) => e.stopPropagation()} style={{ paddingTop: '4px', animation: 'fadeIn 0.3s' }}>
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      onChange={() => toggleSelect(m.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                  </div>
                )}
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <h3 style={{ color: 'var(--primary-purple)', margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {!isRead && <span style={{ background: '#e74c3c', color: 'white', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '4px' }}>NEW</span>}
                      {m.title || '无标题'} 
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-light)', fontWeight: 'normal' }}>(ID: {m.id})</span>
                    </h3>
                    <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                      {new Date(m.created_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-light)' }}>
                    发件人: {m.sender_alias}
                  </div>

                  {isExpanded && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '1rem', animation: 'fadeIn 0.3s' }}>
                      <p style={{ marginBottom: '1rem', whiteSpace: 'pre-wrap', padding: '1rem', background: '#f9f9f9', borderRadius: '8px' }}>
                        {m.content}
                      </p>

                      {replyingId === m.id ? (
                        <div style={{ marginTop: '1rem' }}>
                          <textarea
                            className="form-input"
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            rows="4"
                            placeholder="输入回复内容..."
                            style={{ marginBottom: '1rem' }}
                          ></textarea>
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <button className="btn btn-primary" onClick={() => handleSubmitReply(m.id)}>发送回复</button>
                            <button className="btn btn-outline" onClick={handleCancelReply}>取消</button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {m.reply_content && (
                            <div style={{ background: 'var(--bg-purple-light)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                              <p style={{ fontWeight: 'bold', color: 'var(--dark-purple)', marginBottom: '0.5rem' }}>
                                回复 ({new Date(m.reply_at).toLocaleString()}):
                              </p>
                              <p style={{ whiteSpace: 'pre-wrap' }}>{m.reply_content}</p>
                            </div>
                          )}
                          <button className="btn btn-secondary" onClick={(e) => handleReplyClick(e, m)}>
                            {m.reply_content ? '修改回复' : '回复'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Hidden Export Templates */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        {marshmallows.map((m) => (
          <div
            key={`export-${m.id}`}
            ref={el => exportRefs.current[m.id] = el}
            style={{
              width: '800px',
              padding: '60px',
              background: '#fff',
              fontFamily: '"Microsoft YaHei", sans-serif',
              color: '#333',
              position: 'relative',
              boxSizing: 'border-box'
            }}
          >
            {/* Decorative Header */}
            <div style={{ 
              borderBottom: '2px solid var(--primary-purple)', 
              paddingBottom: '20px',
              marginBottom: '30px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end'
            }}>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>标题</div>
                <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--primary-purple)' }}>{m.title || '无标题'}</h1>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>发件人</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{m.sender_alias}</div>
              </div>
            </div>

            {/* Content Body */}
            <div style={{ 
              minHeight: '300px',
              fontSize: '18px',
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap',
              marginBottom: '40px'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>正文：</div>
              {m.content}
            </div>

            {/* Footer */}
            <div style={{ 
              borderTop: '1px solid #eee',
              paddingTop: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              color: '#999',
              fontSize: '14px'
            }}>
              <div>ID: {m.id}</div>
              <div>{new Date(m.created_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarshmallowAdmin;
